import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { ChevronRight, Users } from "lucide-react";
import { StepHelpers } from "@shared/components/Stepper";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { BUTTON_FIELD_CLASSES, PAGE_SIZE } from "@shared/utils/constants";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { PageHeader } from "@shared/components/PageHeader";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useBulkCommunicationStep1,
  useSaveBulkCommunicationStep1,
  useGetCategory,
  useGetSubCategory,
  useGetPremise,
} from "@features/cx/outstanding/hooks";

export interface Consumer {
  consumerId: number;
  consumerNo: string;
  fullName: string;
  status: string;
  totalOutstanding: number;
  agingBucket: string;
  riskLevel: number | string;
  category?: string;
  subCategory?: string;
  premise?: string;
}

interface SharedAccountSelectionProps {
  type: "notice" | "reminder";
  data?: {
    selectedConsumers?: Consumer[];
  };
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  storageKey?: string;
}
export const SharedAccountSelection: React.FC<SharedAccountSelectionProps> = ({
  type,
  data,
  onDataChange,
  onNext,
  stepHelpers,
  currentStepIndex = 0,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const [selectedConsumers, setSelectedConsumers] = useState<Consumer[]>([]);
  const saveMutation = useSaveBulkCommunicationStep1();
  const currency=useCurrency();

  const [filters, setFilters] = useState({
    page: 1,
    search_data: "",
    pageSize: PAGE_SIZE,
    risk: "",
    status: "",
    agingBucket: "",
    category: "",
    subCategory: "",
    premise: "",
  });

  const filterForm = useForm({
    defaultValues: {
      risk: "",
      status: "",
      agingBucket: "",
      category: "",
      subCategory: "",
      premise: "",
    },
  });

  // Call the API hook
  const { data: categories } = useGetCategory();
  const selectedCategory = filterForm.watch("category");
  const { data: subCategories } = useGetSubCategory(
    selectedCategory ? [selectedCategory] : []
  );
  
  const { data: premiseData } = useGetPremise({
    remote_utility_id: remoteUtilityId,
    config_level: "premise",
  });
  const premises = premiseData?.result || [];
  const { data: apiResponse, isLoading } = useBulkCommunicationStep1({
    remoteUtilityId: remoteUtilityId || "",
    risk: filters.risk || undefined,
    agingBucket: filters.agingBucket || undefined,
    status: filters.status || undefined,
    category: filters.category || undefined,
    subCategory: filters.subCategory || undefined,
    premise: filters.premise || undefined,
    page: filters.page,
    limit: filters.pageSize,
    searchData: filters.search_data || undefined,
  });
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  const consumers = apiResponse?.results || [];
  const totalItems = apiResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / filters.pageSize) || 1;

  const selectedIds = useMemo(() => {
    return selectedConsumers.map((c) => String(c.consumerId));
  }, [selectedConsumers]);

  // Load persistence
  useEffect(() => {
    if (data?.selectedConsumers) {
      setSelectedConsumers(data.selectedConsumers);
    } else if (stepHelpers) {
      const saved = stepHelpers.getStepData<{ selectedConsumers?: Consumer[] }>(
        currentStepIndex,
      );
      if (saved?.selectedConsumers) {
        setSelectedConsumers(saved.selectedConsumers);
      }
    }
  }, [data, stepHelpers, currentStepIndex]);

  const handleSearch = (term: string) => {
    setFilters((prev) => ({ ...prev, search_data: term, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const pagination = useMemo(
    () => ({
      currentPage: filters.page,
      totalPages,
      totalItems,
      itemsPerPage: filters.pageSize,
      startItem:
        totalItems === 0 ? 0 : (filters.page - 1) * filters.pageSize + 1,
      endItem: Math.min(filters.page * filters.pageSize, totalItems),
    }),
    [filters.page, filters.pageSize, totalPages, totalItems],
  );

  const totalSelectedOutstanding = useMemo(() => {
    return selectedConsumers.reduce(
      (sum, c) => sum + Number(c.totalOutstanding || 0),
      0,
    );
  }, [selectedConsumers]);

  // Update stepper state
  const handleSelectionChange = (newSelectedIds: string[], isSelectAll?: boolean) => {
    if (isSelectAll !== undefined) {
      setIsSelectAllChecked(isSelectAll);
    }
    setSelectedConsumers((prev) => {
      const newSelectedSet = new Set(newSelectedIds);
      
      const kept = prev.filter((c) =>
        newSelectedSet.has(String(c.consumerId)),
      );
      
      const keptIds = new Set(kept.map((k) => String(k.consumerId)));
      
      const added = consumers.filter(
        (c) =>
          newSelectedSet.has(String(c.consumerId)) &&
          !keptIds.has(String(c.consumerId)),
      );

      let merged = [...kept, ...added];

      // For Bulk Notice, only Active status is allowed
      if (type === "notice") {
        merged = merged.filter((c) => c.status?.toUpperCase() === "ACTIVE");
      }

      onDataChange?.({
        selectedConsumers: merged,
        isSelectAll
      });
      if (stepHelpers) {
        stepHelpers.setStepData(currentStepIndex, {
          selectedConsumers: merged,
          isSelectAll: isSelectAll !== undefined ? isSelectAll : isSelectAllChecked,
        });
      }

      return merged;
    });
  };

  const handleClearAll = () => {
    handleSelectionChange([], false);
  };

  const columns: TableColumn<Consumer>[] = [
    {
      header: "Account No",
      key: "consumerNo",
      width: "w-[150px]",
    },
    {
      header: "Name",
      key: "fullName",
      render: (value: any) => {
        return <span className="font-semibold text-neutral-800">{value}</span>;
      },
    },
    {
      header: "Category",
      key: "category",
    },
    {
      header: "Sub Category",
      key: "subCategory",
    },
    {
      header: "Premise",
      key: "premise",
    },
    {
      header: "Status",
      key: "status",
      render: (value: any) => {
        return <Badge className={getStatusBadgeClass(value)}>{value}</Badge>;
      },
    },
    {
      header: "Outstanding Amount",
      key: "totalOutstanding",
      render: (value: any) => {
        return (
          <span className="font-bold text-neutral-700">
            {currency} {Number(value)}
          </span>
        );
      },
    },
    {
      header: "Aging Bucket",
      key: "agingBucket",
    },
    {
      header: "Risk",
      key: "riskLevel",
      render: (value: any) => {
        if (value === "High") {
          return (
            <Badge
              variant="outline"
              className="bg-red-50 text-red-700 border-red-200"
            >
              High
            </Badge>
          );
        }
        if (value === "Medium") {
          return (
            <Badge
              variant="outline"
              className="bg-orange-50 text-orange-700 border-orange-200"
            >
              Medium
            </Badge>
          );
        }
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-200"
          >
            Low
          </Badge>
        );
      },
    },
  ];

  const filterFields: FormField[] = useMemo(() => {
    const baseFields: FormField[] = [
      {
        name: "risk",
        label: "Risk Level",
        type: "select",
        placeholder: "All Risk Levels",
        options: [
          { label: "High", value: "high" },
          { label: "Medium", value: "medium" },
          { label: "Low", value: "low" },
        ],
        className: "col-span-12 md:col-span-4",
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "All Statuses",
        options: [
          { label: "Active", value: "ACTIVE" },
          { label: "Inactive", value: "INACTIVE" },
        ],
        className: "col-span-12 md:col-span-4",
      },
      {
        name: "agingBucket",
        label: "Aging Bucket",
        type: "select",
        placeholder: "All Aging Buckets",
        options: [
          { label: "30 Days", value: "30" },
          { label: "60 Days", value: "60" },
          { label: "90 Days", value: "90" },
        ],
        className: "col-span-12 md:col-span-4",
      },
      {
        name: "category",
        label: "Category",
        type: "select",
        placeholder: "All Categories",
        options: categories?.map((c: any) => ({
          label: c.name,
          value: c.code,
        })) || [],
        className: "col-span-12 md:col-span-4",
      },
      {
        name: "subCategory",
        label: "Sub Category",
        type: "select",
        placeholder: "All Sub Categories",
        options: subCategories?.map((sc: any) => ({
          label: sc.name,
          value: sc.code,
        })) || [],
        className: "col-span-12 md:col-span-4",
      },
      {
        name: "premise",
        label: "Premise",
        type: "select",
        placeholder: "All Premises",
        options: premises.map((p: any) => ({
          label: p.name,
          value: p.code,
        })) || [],
        className: "col-span-12 md:col-span-4",
      },
    ];

    return baseFields;
  }, [categories,subCategories,premises]);

  const gridColumns = 12;

  const handleNext = () => {
    if (selectedIds.length === 0) return;
    saveMutation.mutate(
      {
        consumerIds: selectedIds.map(Number),
        remoteUtilityId: remoteUtilityId || "",
        is_select_all: isSelectAllChecked,
      },
      {
        onSuccess: () => {
          onNext?.();
        },
      },
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <PageHeader
        title={type === "notice" ? "Consumer Selection" : "Account Selection"}
        subtitle={
          type === "notice"
            ? "Search and filter consumers to include in this bulk notice"
            : "Search and filter accounts to include in this bulk reminder"
        }
      />

      {/* Review Section */}
      <div className="p-5 border rounded-xl bg-white shadow-sm border-neutral-200 space-y-4">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
            <h3 className="text-md font-bold text-neutral-800 tracking-wider text-xs">
              {type === "notice" ? "Selected Consumers" : "Selected Accounts"}
            </h3>
            <Badge
              variant="secondary"
              className="bg-blue-50 text-blue-700 font-bold border-blue-100"
            >
              {selectedIds.length}
            </Badge>
            <span className="text-xs text-neutral-500 ml-2">
              Total Outstanding:{" "}
              <strong className="text-neutral-800">
                {currency} {totalSelectedOutstanding.toFixed(2)}
              </strong>
            </span>
          </div>
          {selectedIds.length > 0 && (
            <Button
              variant="link"
              className="text-red-500 hover:text-red-600 h-auto p-0 text-xs font-bold tracking-tighter"
              onClick={handleClearAll}
            >
              {type === "notice" ? "Clear All" : "Clear Selection"}
            </Button>
          )}
        </div>

        <GenericTable<Consumer>
          data={consumers}
          loading={isLoading}
          columns={columns}
          rowKey="consumerId"
          selectable={true}
          isRowSelectable={(row) => row.status?.toUpperCase() === "ACTIVE"}
          selectedRows={selectedIds}
          onSelectionChange={handleSelectionChange}
          onSearch={handleSearch}
          searchPlaceholder="Search by Account No or Name..."
          pagination={pagination}
          onPageChange={handlePageChange}
          filterPanel={
            <div className="p-4 bg-neutral-50/50 rounded-lg border border-neutral-100 mb-4">
              <DynamicForm
                form={filterForm}
                fields={filterFields}
                gridColumns={gridColumns}
                onSubmit={(data) => {
                  setFilters((prev) => ({
                    ...prev,
                    ...data,
                    page: 1,
                  }));
                }}
                onReset={() => {
                  filterForm.reset();
                  setFilters({
                    page: 1,
                    search_data: "",
                    pageSize: 10,
                    risk: "",
                    status: "",
                    agingBucket: "",
                    category: "",
                    subCategory: "",
                    premise: "",
                  });
                }}
                config={{
                  classes: BUTTON_FIELD_CLASSES,
                  showSubmitButton: true,
                  showResetButton: true,
                  submitButtonText: "Search",
                  resetButtonText:
                    type === "notice" ? "Clear" : "Clear Filters",
                  spacing: "small",
                }}
              />
            </div>
          }
          getRowClassName={(row) =>
            `text-xs ${type === "notice" && row.status?.toUpperCase() !== "ACTIVE" ? "opacity-60" : ""}`
          }
        />
      </div>

      {/* Footer Navigation */}
      <div className="flex justify-end pt-4 border-t border-gray-100">
        <Button
          onClick={handleNext}
          disabled={selectedIds.length === 0 || saveMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center gap-1.5 rounded-lg px-4 py-2"
        >
          <span>
            {saveMutation.isPending
              ? "Saving Selection..."
              : type === "notice"
                ? "Next: Notice Details"
                : "Next: Channel & Template"}
          </span>
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
};

export default SharedAccountSelection;
